// Wishlist utility functions for React components

export interface WishlistItem {
    productId: string;
    productName: string;
    productPrice: number;
    productImage: string;
    productBrand: string;
    addedAt: string;
}

export interface Product {
    id: string;
    productNamePt?: string;
    productNameEn?: string;
    productPrice?: number;
    productImages?: string[];
    productBrand?: string;
}

/**
 * Add a product to the wishlist
 */
export const addToWishlist = (product: Product, language: 'pt' | 'en' = 'pt'): boolean => {
    try {
        // Get existing wishlist from localStorage
        const wishlistStr = localStorage.getItem('wishlist');
        let wishlist: WishlistItem[] = wishlistStr ? JSON.parse(wishlistStr) : [];
        
        // Check if product already exists in wishlist
        const existingItem = wishlist.find(item => item.productId === product.id);
        
        if (existingItem) {
            showWishlistMessage(
                language === 'pt' 
                    ? 'Produto já está na sua lista de desejos!' 
                    : 'Product is already in your wishlist!',
                'info'
            );
            return false;
        }
        
        // Get product name based on language
        const productName = language === 'pt' 
            ? (product.productNamePt || product.productNameEn || 'Product')
            : (product.productNameEn || product.productNamePt || 'Product');
        
        // Add new item to wishlist
        const wishlistItem: WishlistItem = {
            productId: product.id,
            productName: productName,
            productPrice: product.productPrice || 0,
            productImage: product.productImages && product.productImages.length > 0 
                ? product.productImages[0] 
                : '',
            productBrand: product.productBrand || '',
            addedAt: new Date().toISOString()
        };
        
        wishlist.push(wishlistItem);
        
        // Save updated wishlist to localStorage
        localStorage.setItem('wishlist', JSON.stringify(wishlist));
        
        showWishlistMessage(
            language === 'pt' 
                ? 'Produto adicionado à lista de desejos com sucesso!' 
                : 'Product added to wishlist successfully!',
            'success'
        );
        
        // Update wishlist counter
        updateWishlistCounter();
        
        return true;
    } catch (error) {
        showWishlistMessage(
            language === 'pt' 
                ? 'Falha ao adicionar produto à lista de desejos' 
                : 'Failed to add product to wishlist',
            'error'
        );
        return false;
    }
};

/**
 * Update wishlist counter in the UI
 */
export const updateWishlistCounter = (): void => {
    try {
        const wishlistStr = localStorage.getItem('wishlist');
        const wishlist: WishlistItem[] = wishlistStr ? JSON.parse(wishlistStr) : [];
        const totalItems = wishlist.length;
        
        // Update wishlist counter in navbar
        const wishlistCounter = document.querySelector('.main-menu__wishlist-count');
        if (wishlistCounter) {
            wishlistCounter.textContent = String(totalItems);
        }
        
        // Dispatch custom event for other components to listen
        window.dispatchEvent(new CustomEvent('wishlistUpdated', { 
            detail: { totalItems, wishlist } 
        }));
    } catch (error) {
    }
};

/**
 * Show wishlist message to user
 */
const showWishlistMessage = (message: string, type: 'success' | 'error' | 'info'): void => {
    // Create or update message element
    let messageElement = document.getElementById('wishlist-message');
    if (!messageElement) {
        messageElement = document.createElement('div');
        messageElement.id = 'wishlist-message';
        messageElement.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 15px 20px;
            border-radius: 8px;
            color: white;
            font-weight: 600;
            z-index: 10001;
            max-width: 300px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            animation: slideInFromRight 0.3s ease;
        `;
        document.body.appendChild(messageElement);
    }
    
    // Set message content and style based on type
    messageElement.textContent = message;
    messageElement.style.backgroundColor = type === 'success' ? '#28a745' : 
                                          type === 'error' ? '#dc3545' : '#17a2b8';
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
        if (messageElement) {
            messageElement.style.animation = 'slideOutToRight 0.3s ease';
            setTimeout(() => {
                if (messageElement && messageElement.parentNode) {
                    messageElement.parentNode.removeChild(messageElement);
                }
            }, 300);
        }
    }, 3000);
};

